import React__default from 'react';
import { Icon } from '../../../Icon/Icon.js';
import keycode from 'keycode';
import { IconButton } from '../../../IconButton/IconButton.js';
import { Group } from '../../../Group/Group.js';
import { Menu } from '../../../Menu/Menu.js';
import { sanitizeRowProps } from '../../util.js';
import { InView } from 'react-intersection-observer';

var hasActions = function hasActions(inlineEditingUniqueId, handlers, actions) {
  return !!actions || handlers.onRowCreate || handlers.onRowCopy || handlers.onRowDelete || !!inlineEditingUniqueId || handlers.onRowEdit;
};

var getActions = function getActions(actions, row) {
  if (row === void 0) {
    row = undefined;
  }

  var primary = [];
  var secondary = [];

  if (actions) {
    var visibleActions = actions.filter(function (x) {
      return !!x;
    });

    if (row) {
      visibleActions = visibleActions.filter(function (action) {
        if (action.visible !== undefined) {
          if (typeof action.visible === 'function') {
            return action.visible(row);
          }

          return action.visible;
        }

        return true;
      });
    }

    visibleActions.forEach(function (action) {
      if (action.showOnRow) {
        primary.push(action);
      } else {
        secondary.push(action);
      }
    });
  }

  return [primary, secondary];
};

var getLength = function getLength(inlineEditingUniqueId, handlers, actions) {
  var _getActions = getActions(actions),
      primaryActions = _getActions[0],
      secondaryActions = _getActions[1];

  var length = primaryActions.length + (secondaryActions.length ? 1 : 0);

  if (handlers.onRowCreate) {
    length++;
  }

  if (handlers.onRowCopy) {
    length++;
  }

  if (handlers.onRowDelete) {
    length++;
  }

  if (!!inlineEditingUniqueId || handlers.onRowEdit) {
    length++;

    if (length < 2) {
      length++;
    }
  }

  return length;
};

var getActionProps = function getActionProps(action, row) {
  return {
    'aria-label': typeof action.tooltip === 'function' ? action.tooltip(row) : action.tooltip,
    disabled: typeof action.disabled === 'function' ? action.disabled(row) : action.disabled,
    onClick: function onClick(event) {
      event.persist();
      action.onClick(row, event);
    }
  };
};

var EditModeActions = function EditModeActions() {
  React__default.useEffect(function () {
    var listener = function listener(event) {
      var _event$target, _event$target$form;

      if (event.keyCode === keycode('escape') && (_event$target = event.target) !== null && _event$target !== void 0 && (_event$target$form = _event$target.form) !== null && _event$target$form !== void 0 && _event$target$form.reset) {
        event.preventDefault();
        event.target.form.reset();
      }
    };

    document.addEventListener('keydown', listener);
    return function () {
      document.removeEventListener('keydown', listener);
    };
  }, []);
  return React__default.createElement(Group, null, React__default.createElement(IconButton, {
    appearance: "primary",
    icon: "tick",
    type: "submit",
    className: "mr-2"
  }), React__default.createElement(IconButton, {
    appearance: "ghost",
    icon: "close",
    type: "reset"
  }));
};

var useRowActions = function useRowActions(inlineEditingUniqueId, handlers, actions, rowExpansionRenderer, texts, windowed) {
  if (windowed === void 0) {
    windowed = false;
  }

  var inlineEditing = !!inlineEditingUniqueId;
  var actionsLength = getLength(inlineEditingUniqueId, handlers, actions);

  var plugin = function plugin(hooks) {
    if (hasActions(inlineEditingUniqueId, handlers, actions)) {
      hooks.visibleColumns.push(function (columns) {
        return [].concat(columns, [{
          id: '_actions',
          className: 'yt-table__cell__actions',
          flex: "0 0 calc((" + actionsLength + " * 2rem) + .5rem)",
          Cell: function Cell(_ref) {
            var row = _ref.row;

            if (row.isEditing) {
              return React__default.createElement(EditModeActions, null);
            }

            var sanitizedRow = sanitizeRowProps(row, rowExpansionRenderer);

            var _getActions2 = getActions(actions, sanitizedRow),
                primaryActions = _getActions2[0],
                secondaryActions = _getActions2[1];

            var output = React__default.createElement(React__default.Fragment, null, handlers.onRowCreate && React__default.createElement(IconButton, {
              appearance: "discrete",
              icon: "plus-circle",
              "aria-label": texts.table.newSubRow,
              tooltip: texts.table.newSubRow,
              disabled: inlineEditing && !row.canEdit,
              onClick: function onClick(event) {
                event.stopPropagation();
                event.persist();
                row.setActive();

                if (!row.isExpanded) {
                  row.toggleRowExpanded();
                }

                handlers.onRowCreate(sanitizedRow, event);
              }
            }), (inlineEditing || handlers.onRowEdit) && React__default.createElement(IconButton, {
              appearance: "discrete",
              icon: "edit",
              "aria-label": texts.table.edit,
              tooltip: texts.table.edit,
              disabled: inlineEditing && !row.canEdit,
              onClick: function onClick(event) {
                event.stopPropagation();
                row.setActive();

                if (inlineEditing) {
                  row.toggleRowEditing();
                } else if (handlers.onRowEdit) {
                  event.persist();
                  handlers.onRowEdit(sanitizedRow, event);
                }
              }
            }), handlers.onRowCopy && React__default.createElement(IconButton, {
              appearance: "discrete",
              icon: "copy",
              "aria-label": texts.table.copy,
              tooltip: texts.table.copy,
              disabled: inlineEditing && !row.canEdit,
              onClick: function onClick(event) {
                event.stopPropagation();
                event.persist();
                row.setActive();
                handlers.onRowCopy(sanitizedRow, event);
              }
            }), handlers.onRowDelete && React__default.createElement(IconButton, {
              appearance: "discrete",
              icon: "delete",
              "aria-label": texts.table.del,
              tooltip: texts.table.del,
              disabled: inlineEditing && !row.canEdit,
              onClick: function onClick(event) {
                event.stopPropagation();
                event.persist();
                row.setActive();
                handlers.onRowDelete(sanitizedRow, event);
              }
            }), primaryActions.map(function (action, index) {
              return React__default.createElement(IconButton, Object.assign({}, getActionProps(action, sanitizedRow), {
                key: index,
                appearance: "discrete",
                icon: typeof action.icon === 'function' ? action.icon(sanitizedRow) : action.icon,
                tooltip: typeof action.tooltip === 'function' ? action.tooltip(sanitizedRow) : action.tooltip
              }));
            }), secondaryActions.length ? React__default.createElement(IconButton, {
              appearance: "discrete",
              icon: "ellipsis-vertical",
              "aria-label": texts.table.actions,
              tooltip: texts.table.actions,
              disabled: inlineEditing && !row.canEdit,
              onClick: function onClick(event) {
                event.stopPropagation();
                row.setActive();
              },
              menu: function menu(menuProps) {
                return React__default.createElement(Menu, Object.assign({}, menuProps), secondaryActions.map(function (action, index) {
                  return React__default.createElement(Menu.Item, Object.assign({
                    key: index
                  }, getActionProps(action, sanitizedRow)), React__default.createElement(Icon, {
                    name: typeof action.icon === 'function' ? action.icon(sanitizedRow) : action.icon
                  }), action.text);
                }));
              }
            }) : null);

            if (windowed) {
              return React__default.createElement(Group, null, output);
            }

            return React__default.createElement(InView, {
              rootMargin: "100px 0px"
            }, function (_ref2) {
              var inView = _ref2.inView,
                  ref = _ref2.ref;
              return React__default.createElement(Group, {
                ref: ref
              }, inView ? output : null);
            });
          }
        }]);
      });
    }
  };

  plugin.pluginName = 'useRowActions';
  return plugin;
};

export { useRowActions };
//# sourceMappingURL=useRowActions.js.map
